import facetFields from '@/search/facetFields'

// Meilisearch's array-of-arrays filter syntax: the inner array is OR'd, the outer
// array is AND'd — so checking multiple values within one facet broadens results,
// while checking values across different facets narrows them.
export function buildFacetFilter(filterData): string[][] {
  return filterData.reduce((acc, filter) => {
    const checked = filter.values.filter((v) => v.isChecked).map((v) => v.value)
    if (checked.length > 0) {
      acc.push(checked.map((v) => `${filter.predicate} = "${String(v).replace(/"/g, '\\"')}"`))
    }
    return acc
  }, [])
}

// Converts Meilisearch's facetDistribution ({ field: { value: count } }) into the
// { predicate, label, type, values: [{ value, label, count, isChecked }] } shape
// the facet UI renders, using facetFields.ts for labels/ordering and preserving
// any previously-checked state. Checked values are kept even when the distribution
// no longer contains them (e.g. a combined filter matches zero documents) so the
// user's selection - and Clear All / Clear - stays visible and can be undone.
export function mergeFacetDistribution(filterData, facetDistribution) {
  return facetFields.map((f) => {
    const existing = filterData.find((d) => d.predicate === f.predicate)
    const distribution = facetDistribution[f.predicate] || {}
    const checkedValues = (existing?.values ?? [])
      .filter((v) => v.isChecked)
      .map((v) => v.value)
    const allValues = Array.from(new Set([...Object.keys(distribution), ...checkedValues]))
    const values = allValues.map((value) => ({
      value,
      label: value,
      count: distribution[value] ?? 0,
      isChecked: checkedValues.includes(value),
    }))
    return {
      predicate: f.predicate, label: f.label, description: f.description, type: 'LITERAL', values,
    }
  })
}

// Turn a Meilisearch hit into the { label, value } that
// can be rendered as <dl> entries
export function buildHitMetadata(hit) {
  const resultItemFields = [
    {
      predicate: 'numberOfUniqueIndividuals',
      label: 'Number of Patients',
    },
    {
      predicate: 'dataType',
      label: 'Data Type(s)',
    },
    {
      predicate: 'mediaType',
      label: 'Media Type',
    },
    {
      predicate: 'format',
      label: 'Format',
    },
  ]
  const metadata = resultItemFields
    .reduce((acc, f) => {
      const value = hit[f.predicate]
      if (value === undefined || value === null || (Array.isArray(value) && value.length === 0)) {
        return acc
      }
      acc.push({ label: f.label, value: Array.isArray(value) ? value.join(', ') : value })
      return acc
    }, [])

  return metadata
}
